import { useState, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  GitCompareArrows,
  CheckSquare,
  Square,
  Search,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScoreBadge } from '@/features/evaluation/components/ScoreBar';
import { ComparisonView } from '@/features/evaluation/components/ComparisonView';
import { CrossDocSynthesis } from '@/features/evaluation/components/CrossDocSynthesis';
import { useEvaluations } from '@/features/evaluation/hooks/useEvaluation';
import { useSources } from '@/features/sources/hooks/useSources';
import { useAuthStore } from '@/store/authStore';
import type { EvaluationWithDetails, EvaluationCriterion } from '@/features/evaluation/types';

// =========================================
// Compare Page — select sources & compare
// =========================================

export function Compare() {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  // Fetch completed evaluations
  const { data: evaluations = [], isLoading: evalsLoading } = useEvaluations({
    status: 'completed',
  });

  // Fetch sources for display info
  const { data: sources = [] } = useSources();

  // Build enriched evaluation list with source names
  const evaluatedSources = useMemo(() => {
    return evaluations
      .filter((ev) => ev.score !== null)
      .map((ev) => {
        const source = sources.find((s) => s.id === ev.source_id);
        return {
          evaluation_id: ev.id,
          source_id: ev.source_id,
          name: source?.title || source?.file_name || 'Untitled',
          authors: (source?.authors as string[]) ?? [],
          score: ev.breakdown
            ? (ev.breakdown as { percentage: number }).percentage
            : (ev.score ?? 0),
        };
      })
      .filter(
        (item) =>
          !search ||
          item.name.toLowerCase().includes(search.toLowerCase()) ||
          item.authors.some((a) => a.toLowerCase().includes(search.toLowerCase()))
      );
  }, [evaluations, sources, search]);

  const toggleSource = (evaluationId: string) => {
    setSelectedIds((prev) =>
      prev.includes(evaluationId)
        ? prev.filter((id) => id !== evaluationId)
        : [...prev, evaluationId]
    );
  };

  const selectAll = () => {
    const allIds = evaluatedSources.map((s) => s.evaluation_id);
    setSelectedIds(allIds);
  };

  const clearAll = () => setSelectedIds([]);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <GitCompareArrows className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Compare Sources</h1>
          <p className="text-sm text-slate-500">
            Select evaluated sources to compare their scores side by side.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left panel: source selector */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Select Sources</CardTitle>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-1 max-h-[500px] overflow-y-auto">
              {/* Actions */}
              <div className="flex items-center gap-2 mb-2">
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={selectAll}>
                  Select all
                </Button>
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={clearAll}>
                  Clear
                </Button>
                <Badge variant="outline" className="ml-auto text-[10px]">
                  {selectedIds.length} selected
                </Badge>
              </div>
              <Separator className="mb-2" />

              {evalsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : evaluatedSources.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">
                    No evaluated sources found. Run evaluations first.
                  </p>
                </div>
              ) : (
                evaluatedSources.map((item) => {
                  const isSelected = selectedIds.includes(item.evaluation_id);
                  return (
                    <button
                      key={item.evaluation_id}
                      type="button"
                      className={cn(
                        'w-full flex items-start gap-2 p-2 rounded-md text-left transition-colors',
                        isSelected
                          ? 'bg-indigo-50 border border-indigo-200'
                          : 'hover:bg-slate-50 border border-transparent'
                      )}
                      onClick={() => toggleSource(item.evaluation_id)}
                    >
                      {isSelected ? (
                        <CheckSquare className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
                      ) : (
                        <Square className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-slate-700 truncate">
                          {item.name}
                        </p>
                        {item.authors.length > 0 && (
                          <p className="text-[10px] text-slate-400 truncate">
                            {item.authors.slice(0, 2).join(', ')}
                            {item.authors.length > 2 && ' et al.'}
                          </p>
                        )}
                      </div>
                      <ScoreBadge score={item.score} />
                    </button>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right panel: comparison table */}
        <div className="lg:col-span-3">
          <ComparisonViewLoader evaluationIds={selectedIds} />
        </div>
      </div>
    </div>
  );
}

// =========================================
// Loader — fetches full evaluation details
// =========================================

interface ComparisonViewLoaderProps {
  evaluationIds: string[];
}

function ComparisonViewLoader({ evaluationIds }: ComparisonViewLoaderProps) {
  const user = useAuthStore((s) => s.user);

  // Use useQueries to fetch all evaluations in parallel (hooks-safe)
  const results = useQueries({
    queries: evaluationIds.map((evalId) => ({
      queryKey: ['evaluation', evalId],
      queryFn: async (): Promise<EvaluationWithDetails | null> => {
        if (!evalId || !user) return null;

        const { data: evaluation, error: evalError } = await supabase
          .from('evaluations')
          .select('*')
          .eq('id', evalId)
          .single();

        if (evalError) throw evalError;
        if (!evaluation) return null;

        const { data: source } = await supabase
          .from('sources')
          .select('id, file_name, title, authors')
          .eq('id', evaluation.source_id)
          .single();

        const { data: criteriaResults } = await supabase
          .from('evaluation_criteria')
          .select('*')
          .eq('evaluation_id', evalId);

        return {
          ...evaluation,
          source: source ?? undefined,
          criteria_results: (criteriaResults ?? []) as EvaluationCriterion[],
        } as EvaluationWithDetails;
      },
      enabled: !!evalId && !!user,
    })),
  });

  const isLoading = results.some((q) => q.isLoading);
  const evaluations = results
    .map((q) => q.data)
    .filter((d): d is EvaluationWithDetails => d !== null && d !== undefined);

  if (evaluationIds.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <GitCompareArrows className="h-12 w-12 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No sources selected</p>
          <p className="text-sm text-slate-400 mt-1">
            Choose 2 or more evaluated sources from the left panel to compare.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading evaluation details...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <ComparisonView evaluations={evaluations} />
      <CrossDocSynthesis evaluations={evaluations} />
    </div>
  );
}
